/**
 * Token Manager - Handles automatic token expiration detection and refresh
 * Automatically redirects to login when tokens expire and can't be refreshed
 */

import { cacheBackendToken as cacheToken, clearBackendTokenCache } from "./tokenCache";
import { BACKEND_URL } from "@/lib/api/client";
import { isTestAuth } from "@/lib/testAuth";

interface SessionTokenInfo {
  accessToken?: string;
  accessTokenExpires?: number;
  refreshToken?: string;
  error?: string;
}

const BACKEND_TOKEN_KEY = 'backendToken';
const BACKEND_TOKEN_EXPIRES_KEY = 'backendTokenExpires';
const MS_TOKEN_EXPIRES_KEY = 'msTokenExpires';

/**
 * Decode JWT to get expiration time (without verification)
 */
function getTokenExpiration(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = JSON.parse(atob(parts[1]));
    if (payload.exp) {
      return payload.exp * 1000;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Check if a token is expired (with 5 minute buffer for refresh attempts)
 */
function isTokenExpired(expiresAt: number | null, bufferMinutes: number = 5): boolean {
  if (!expiresAt) return true;
  const bufferMs = bufferMinutes * 60 * 1000;
  return Date.now() >= (expiresAt - bufferMs);
}

/**
 * Get backend JWT token expiration
 */
export function getBackendTokenExpiration(): number | null {
  const storedExpires = localStorage.getItem(BACKEND_TOKEN_EXPIRES_KEY);
  if (storedExpires) {
    const expiresAt = parseInt(storedExpires, 10);
    if (!isNaN(expiresAt)) {
      return expiresAt;
    }
  }

  const token = localStorage.getItem(BACKEND_TOKEN_KEY);
  if (token) {
    const expiresAt = getTokenExpiration(token);
    if (expiresAt) {
      localStorage.setItem(BACKEND_TOKEN_EXPIRES_KEY, expiresAt.toString());
      return expiresAt;
    }
  }

  return null;
}

/**
 * Get Microsoft Graph token expiration from session
 */
export function getMicrosoftTokenExpiration(session: SessionTokenInfo | null): number | null {
  if (!session) return null;

  if (session.accessTokenExpires) {
    return session.accessTokenExpires;
  }

  if (session.accessToken) {
    return getTokenExpiration(session.accessToken);
  }

  return null;
}

/**
 * Check if tokens are expired and need refresh
 */
export function checkTokenExpiration(session: SessionTokenInfo | null): {
  backendExpired: boolean;
  msExpired: boolean;
  needsReauth: boolean;
} {
  if (!session) {
    return {
      backendExpired: false,
      msExpired: false,
      needsReauth: false,
    };
  }

  const backendExpiresAt = getBackendTokenExpiration();
  const msExpiresAt = getMicrosoftTokenExpiration(session);

  const backendExpired = backendExpiresAt ? isTokenExpired(backendExpiresAt) : false;
  const msExpired = msExpiresAt ? isTokenExpired(msExpiresAt) : false;

  const needsReauth = msExpired && backendExpired && (!session?.refreshToken);

  return {
    backendExpired,
    msExpired,
    needsReauth,
  };
}

// Track ongoing refresh attempts to prevent multiple simultaneous refreshes
let refreshInProgress = false;
let lastRefreshAttempt = 0;
const REFRESH_COOLDOWN = 5000;

/**
 * Attempt to refresh backend token by exchanging Microsoft token
 */
async function refreshBackendToken(microsoftToken: string): Promise<string | null> {
  const now = Date.now();
  if (refreshInProgress) {
    let waited = 0;
    while (refreshInProgress && waited < 5000) {
      await new Promise(resolve => setTimeout(resolve, 100));
      waited += 100;
    }
    if (!refreshInProgress) {
      const token = localStorage.getItem(BACKEND_TOKEN_KEY);
      if (token) return token;
    }
    return null;
  }

  if (now - lastRefreshAttempt < REFRESH_COOLDOWN) {
    return null;
  }

  refreshInProgress = true;
  lastRefreshAttempt = now;

  try {
    const response = await fetch(`${BACKEND_URL}/api/auth/microsoft`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ accessToken: microsoftToken }),
    });

    if (response.status === 401) {
      refreshInProgress = false;
      const { handle401Error } = await import('./apiErrorHandler');
      handle401Error();
      return null;
    }

    if (response.status === 403) {
      refreshInProgress = false;
      const { handle403Error } = await import('./apiErrorHandler');
      const errorData = await response.json().catch(() => ({ detail: 'Consent required' }));
      handle403Error(errorData.detail);
      return null;
    }

    if (response.ok) {
      const data = await response.json();
      const newToken = data.data?.token;

      if (newToken) {
        cacheToken(newToken);
        refreshInProgress = false;
        return newToken;
      }
    }

    refreshInProgress = false;
    return null;
  } catch {
    refreshInProgress = false;
    return null;
  }
}

/**
 * Handle token expiration - attempt refresh or redirect to login
 */
export async function handleTokenExpiration(session: SessionTokenInfo | null): Promise<boolean> {
  if (!session) {
    return true;
  }

  if (isTestAuth()) {
    return true;
  }

  if (session?.error === "RefreshAccessTokenError" || session?.error === "NoToken") {
    if (window.location.pathname !== '/login') {
      const { localLogout } = await import("@/lib/localLogout");
      await localLogout("/login?expired=true");
    }
    return false;
  }

  const { backendExpired, msExpired, needsReauth } = checkTokenExpiration(session);

  if (needsReauth) {
    if (window.location.pathname !== '/login') {
      const { localLogout } = await import("@/lib/localLogout");
      await localLogout("/login?expired=true");
    }
    return false;
  }

  if (backendExpired && !msExpired && session?.accessToken) {
    if (window.location.pathname === '/login') {
      return false;
    }

    try {
      const newToken = await refreshBackendToken(session.accessToken);
      return !!newToken;
    } catch {
      return false;
    }
  }

  return true;
}

/**
 * Initialize token expiration monitoring
 */
export function initializeTokenMonitoring(session: SessionTokenInfo | null, checkInterval: number = 60000) {
  const interval = setInterval(() => {
    if (window.location.pathname !== '/login') {
      handleTokenExpiration(session).catch(() => {});
    }
  }, checkInterval);

  return () => clearInterval(interval);
}

/**
 * Clear all stored tokens (for logout)
 */
export function clearAllTokens() {
  localStorage.removeItem(BACKEND_TOKEN_KEY);
  localStorage.removeItem(BACKEND_TOKEN_EXPIRES_KEY);
  localStorage.removeItem(MS_TOKEN_EXPIRES_KEY);

  clearBackendTokenCache();
}
